import React from 'react';
import styled from 'styled-components';

import { Typography } from 'src/typography';
import { Button } from 'components/core';
import useTranslation from 'hooks/use-translation';

export type BelongsToOtherProps = {
  /**
   * Backend-formatted hint (one grapheme + `•••` + intact domain, e.g.
   * `j•••@gmail.com`) -- rendered verbatim, never re-masked. Nullable on the
   * wire like every other hint field; falls back to a hint-free sentence
   * when absent.
   */
  ownerEmailHint?: string | null;
  onLoginToOtherAccount: () => void;
  onChangePhone: () => void;
  onUpdateLater: () => void;
};

/**
 * Leg 2 landing screen: the number on file is already verified to another
 * account. The real "switch account" mechanism (User.logout / clearStore /
 * open the login modal) is deliberately not built here -- see
 * `onLoginToOtherAccount` on the parent modal -- that belongs to the ticket
 * that also owns the trigger (FINTECH-4328).
 */
export const BelongsToOther = ({
  ownerEmailHint,
  onLoginToOtherAccount,
  onChangePhone,
  onUpdateLater,
}: BelongsToOtherProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Container data-testid='phone-verification-nudge-belongs-to-other'>
      <Heading size='medium' weight='bold' tag='h2'>
        {t('phoneVerificationNudge.belongsToOtherTitle', 'This phone number is connected to another account')}
      </Heading>
      {ownerEmailHint ? (
        <Body data-testid='phone-verification-nudge-belongs-to-other-body'>
          {t('phoneVerificationNudge.belongsToOtherLoginWith', 'Login with account')} {ownerEmailHint}
          <br />
          {t('phoneVerificationNudge.belongsToOtherOrChange', 'or change your phone number.')}
        </Body>
      ) : (
        <Body data-testid='phone-verification-nudge-belongs-to-other-body'>
          {t(
            'phoneVerificationNudge.belongsToOtherLoginWithNoHint',
            'Login with the account already using this phone number, or change your phone number.'
          )}
        </Body>
      )}
      <Button
        onClick={onLoginToOtherAccount}
        width='100%'
        data-testid='phone-verification-nudge-belongs-to-other-login'
      >
        {t('phoneVerificationNudge.belongsToOtherLoginButton', 'Login to new account')}
      </Button>
      <Button
        onClick={onChangePhone}
        variant='outlinePrimary'
        width='100%'
        data-testid='phone-verification-nudge-belongs-to-other-change-phone'
      >
        {t('phoneVerificationNudge.belongsToOtherChangePhoneButton', 'Change phone')}
      </Button>
      <UpdateLaterButton
        type='button'
        onClick={onUpdateLater}
        data-testid='phone-verification-nudge-belongs-to-other-update-later'
      >
        {t('phoneVerificationNudge.belongsToOtherUpdateLater', 'Update number later')}
      </UpdateLaterButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Heading = styled(Typography.Heading)`
  align-self: flex-start;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  margin: 0;
  padding-right: 32px;
  text-align: left;
`;

const Body = styled.p`
  color: var(--dcm-modal-secondary-text-color, ${({ theme }) => theme.colors.grey[30]});
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  text-align: center;
`;

const UpdateLaterButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--dcm-modal-secondary-text-color, ${({ theme }) => theme.colors.grey[30]});
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
`;
